import { Cart, Order } from 'shared/types/cart';
import { ExtendedLineItem } from 'shared/types/cart/LineItem';
import { ExtendedProductDetailProduct, ExtendedProductListingProduct } from 'shared/types/product/Product';
import { PaymentData } from 'components/commercetools-ui/organisms/checkout/provider/payment/types';
import {
  ADD_PAYMENT_INFO,
  ADD_SHIPPING_INFO,
  ADD_TO_CART,
  BEGIN_CHECKOUT,
  LOGIN,
  PURCHASE,
  REMOVE_FROM_CART,
  SELECT_ITEM,
  SIGN_UP,
  VIEW_CART,
  VIEW_ITEM,
  VIEW_ITEM_LIST,
  VIEW_ORDER_DETAILS,
  VIEW_ORDER_LIST,
} from 'helpers/constants/events';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import { DEFAULT_CURRENCY } from '../../../helpers/constants/ecommerce';

type GTM_LineItem = {
  item_id: string;
  item_name: string;
  item_variant?: string;
  item_sku?: string;
  item_variant_type?: string;
  index?: number;
  purchasable_status: boolean;
  starting_price?: number;
  price: number;
  quantity?: number;
  discount?: number;
  currency?: string;
};

const normalizeLineItem = (lineItem: ExtendedLineItem): GTM_LineItem => {
  return {
    item_id: lineItem?.productId ?? '',
    item_name: lineItem?.name ?? '',
    item_variant: lineItem?.variants?.length === 1 ? (lineItem.variants[0].sku ?? '') : '',
    item_variant_type: (lineItem?.variants?.length ?? 0) > 1 ? 'Variant Group' : 'Single Product',
    item_sku: (lineItem?.variants?.length ?? 0) > 1 ? (lineItem?.variants?.[0].sku ?? '') : lineItem?.variant?.sku,
    purchasable_status: lineItem?.price !== null, // TODO: Check if product is purchasable backend side
    starting_price: CurrencyHelpers.centAmountToNumber(lineItem?.price?.centAmount ?? 0),
    price: CurrencyHelpers.centAmountToNumber(lineItem?.price?.centAmount ?? 0),
    quantity: lineItem.count ?? 0,
    discount: CurrencyHelpers.centAmountToNumber(lineItem?.discountedPrice?.centAmount ?? 0),
    currency: lineItem.price?.currencyCode ?? DEFAULT_CURRENCY,
  };
};

const normalizeViewCartItems = (cart: Cart): GTM_LineItem[] => {
  return (
    cart.lineItems?.map((lineItem, index) => {
      return {
        index,
        ...normalizeLineItem(lineItem),
      };
    }) ?? []
  );
};

const normalizeProduct = (product: ExtendedProductListingProduct): GTM_LineItem => {
  const price = product?.startingAtPrice?.salePrice ? Number(product?.startingAtPrice?.salePrice) : 0;
  return {
    item_id: product.productId ?? '',
    item_name: product.name ?? '',
    item_sku: product.variants.length === 1 ? (product.variants[0].sku ?? '') : '',
    item_variant: product.variants.length === 1 ? (product.variants[0].sku ?? '') : '',
    item_variant_type: (product.variants.length ?? 0) > 1 ? 'Variant Group' : 'Single Product',
    purchasable_status: product.variants[0].price !== null, // TODO: Check if product is purchasable backend side
    starting_price: CurrencyHelpers.centAmountToNumber(product.startingAtPrice?.basePriceNumeric ?? 0),
    price: CurrencyHelpers.centAmountToNumber(price),
    quantity: 1,
    discount: CurrencyHelpers.centAmountToNumber(product?.startingAtPrice?.discountPercentage ?? 0),
  };
};

const normalizePaymentType = (payment: PaymentData) => {
  if (payment.type === 'scheme') {
    return 'Credit Card';
  }
  if (payment.type === 'paypal') {
    return 'PayPal';
  }
  return 'Unknown';
};

const getSubtotalPrice = (order: Order | Cart) => {
  let subtotalPrice = order.sum?.centAmount ?? 0;
  // Since .sum includes the sum of lineItems*quantity + possible shipping value, we remove it to get the subtotal.
  if (order.shippingInfo?.price?.centAmount && subtotalPrice) subtotalPrice -= order.shippingInfo.price.centAmount;
  return subtotalPrice;
};

const useGTM = () => {
  const trackEvent = (event: string, eventParams: Record<string, any>) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null }); // Clear previous event's data
    window.dataLayer.push({ event, ecommerce: eventParams });
  };

  const viewItemListEvent = (products: ExtendedProductListingProduct[], searchParam?: string) => {
    trackEvent(VIEW_ITEM_LIST, {
      currency: DEFAULT_CURRENCY, // TODO: Get currency from cart
      items: products.map((product) => normalizeProduct(product)),
      ...(searchParam && { searchParam }),
    });
  };

  const selectItemEvent = (product: ExtendedProductListingProduct) => {
    trackEvent(SELECT_ITEM, {
      items: [normalizeProduct(product)],
    });
  };

  const addToCartEvent = (cart: Cart) => {
    trackEvent(ADD_TO_CART, { items: normalizeViewCartItems(cart) });
  };

  const viewItemEvent = (product: ExtendedProductDetailProduct) => {
    trackEvent(VIEW_ITEM, { items: [normalizeLineItem(product)] });
  };

  const viewCartEvent = (cart: Cart) => {
    trackEvent(VIEW_CART, {
      currency: cart.sum?.currencyCode ?? DEFAULT_CURRENCY,
      value: CurrencyHelpers.centAmountToNumber(getSubtotalPrice(cart)),
      items: normalizeViewCartItems(cart),
    });
  };

  const removeFromCartEvent = (lineItem: ExtendedLineItem) => {
    trackEvent(REMOVE_FROM_CART, {
      currency: lineItem.price?.currencyCode ?? DEFAULT_CURRENCY,
      value: CurrencyHelpers.centAmountToNumber(lineItem.price?.centAmount ?? 0),
      items: [normalizeLineItem(lineItem)],
    });
  };

  const beginCheckoutEvent = (cart: Cart) => {
    trackEvent(BEGIN_CHECKOUT, {
      currency: cart.sum?.currencyCode ?? DEFAULT_CURRENCY,
      value: CurrencyHelpers.centAmountToNumber(getSubtotalPrice(cart)),
      coupon: cart.discountCodes?.[0]?.code ?? '',
      items: normalizeViewCartItems(cart),
    });
  };

  const addShippingInfoEvent = (cart: Cart) => {
    trackEvent(ADD_SHIPPING_INFO, {
      currency: cart.sum?.currencyCode ?? DEFAULT_CURRENCY,
      value: CurrencyHelpers.centAmountToNumber(getSubtotalPrice(cart)),
      shipping_tier: cart.shippingInfo?.shippingMethodName ?? cart.shippingInfo?.name ?? '',
      items: normalizeViewCartItems(cart),
    });
  };

  const addPaymentInfoEvent = (cart: Cart, paymentData: PaymentData) => {
    trackEvent(ADD_PAYMENT_INFO, {
      currency: cart.sum?.currencyCode ?? DEFAULT_CURRENCY,
      value: CurrencyHelpers.centAmountToNumber(getSubtotalPrice(cart)),
      coupon: cart.discountCodes?.[0]?.code ?? '',
      payment_type: normalizePaymentType(paymentData),
      items: normalizeViewCartItems(cart),
    });
  };

  const purchaseEvent = (order: Order) => {
    trackEvent(PURCHASE, {
      currency: order.sum?.currencyCode ?? DEFAULT_CURRENCY,
      value: CurrencyHelpers.centAmountToNumber(getSubtotalPrice(order)),
      coupon: order.discountCodes?.[0]?.code ?? '',
      items: normalizeViewCartItems(order),
      transaction_id: order?.orderId ?? '',
      shipping: CurrencyHelpers.centAmountToNumber(order?.shippingInfo?.price?.centAmount ?? 0),
      tax: CurrencyHelpers.centAmountToNumber(order?.taxed?.taxAmount?.centAmount ?? 0),
    });
  };

  const loginEvent = (method: string) => {
    trackEvent(LOGIN, {
      method,
    });
  };

  const signUpEvent = (method: string) => {
    trackEvent(SIGN_UP, {
      method,
    });
  };

  const viewOrderListEvent = (orders: Order[]) => {
    trackEvent(VIEW_ORDER_LIST, {
      orders: orders.map((order) => ({
        order_id: order?.orderId ?? '',
      })),
    });
  };

  const viewOrderEvent = (order: Order) => {
    trackEvent(VIEW_ORDER_DETAILS, {
      order_id: order?.orderId ?? '',
    });
  };

  return {
    trackEvent,
    viewItemListEvent,
    selectItemEvent,
    addToCartEvent,
    viewItemEvent,
    viewCartEvent,
    removeFromCartEvent,
    beginCheckoutEvent,
    addShippingInfoEvent,
    addPaymentInfoEvent,
    purchaseEvent,
    loginEvent,
    signUpEvent,
    viewOrderListEvent,
    viewOrderEvent,
  };
};

export default useGTM;
